import { cn } from '@/lib/utils'

// Same rounded pill and --status-* pairs as StatusBadge (POs) and
// StocktakeBadges, grouped by whose move it is (docs/designs/colour-pass.md):
// blue still being worked on, green done, red a real failure, grey neutral.
const BASE =
  'inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none whitespace-nowrap'

const DRAFT = 'bg-[var(--status-draft-bg)] text-[var(--status-draft-fg)]'
const APPROVED = 'bg-[var(--status-approved-bg)] text-[var(--status-approved-fg)]'
const REJECTED = 'bg-[var(--status-rejected-bg)] text-[var(--status-rejected-fg)]'
const ARCHIVED = 'bg-[var(--status-archived-bg)] text-[var(--status-archived-fg)]'

/** The record's own state: a draft is still editable (blue, in progress), a
 *  performed record is the receipt of a push Shopify already took (green). */
export function AdjustmentStateBadge({
  status,
  className,
}: {
  status: string
  className?: string
}) {
  const [label, style] =
    status === 'performed' ? ['Performed', APPROVED] : ['Draft', DRAFT]
  return (
    <span className={cn(BASE, style, className)} data-adjustment-state={status}>
      {label}
    </span>
  )
}

/** The Variants column badge. Stocky shows a gray 0 rather than hiding the
 *  record: a count that found no discrepancies still files an adjustment, and
 *  dropping it would leave a hole in the audit trail. A count is not a status,
 *  so every value uses the neutral grey pair; 0 is muted further. */
export function VariantCountBadge({
  count,
  className,
}: {
  count: number
  className?: string
}) {
  return (
    <span
      className={cn(
        BASE,
        ARCHIVED,
        count === 0 && 'text-[var(--mute)]',
        'tabular-nums',
        className
      )}
      data-variant-count={count}
    >
      {count}
    </span>
  )
}

/** Per-line Status on the detail view. Only a line Shopify actually took reads
 *  "Adjusted"; a line the apply deliberately passed over reads "Skipped" (grey,
 *  nothing moved, nothing wrong); a draft line hasn't been tried yet, so it
 *  stays a muted dash rather than claiming anything. */
export function LineStatusBadge({
  status,
  className,
}: {
  status: string | null
  className?: string
}) {
  if (status === 'adjusted') {
    return (
      <span className={cn(BASE, APPROVED, className)} data-line-status={status}>
        Adjusted
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span className={cn(BASE, REJECTED, className)} data-line-status={status}>
        Failed
      </span>
    )
  }
  if (status === 'skipped_zero' || status === 'skipped_no_target') {
    return (
      <span className={cn(BASE, ARCHIVED, className)} data-line-status={status}>
        Skipped
      </span>
    )
  }
  return (
    <span
      className={cn('text-xs text-[var(--mute)]', className)}
      data-line-status={status ?? 'draft'}
    >
      —
    </span>
  )
}
